import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box } from '@mui/system';
import Typography from '@mui/material/Typography';
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import useMediaQuery from '@mui/material/useMediaQuery';

const FinancialStatsCards = () => {
  const [user, setUser] = useState({});
  const [token, setToken] = useState(() => localStorage.getItem('token') || null);
  const [fetchedData, setFetchedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonthSales, setCurrentMonthSales] = useState(0);
  const [previousMonthSales, setPreviousMonthSales] = useState(0);
  const [salesGrowth, setSalesGrowth] = useState(0);
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery('(max-height: 600px)');

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
    }
  }, [token]);

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem("token");
          navigate("/");
        }
      }
    };

    fetchProfile();
  }, [navigate]);

  useEffect(() => {
    if (user.id) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const response = await axios.get('https://admin.bizstor.lk/public/api/get-data');
          const userOrders = response.data.filter(
            (order) => order.usertoken.toLowerCase() === user.id.toString().toLowerCase()
          );
          setFetchedData(userOrders);
          calculateSalesData(userOrders);
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [user.id]);

  const calculateSalesData = (data) => {
    if (!data.length) return;

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const prevMonthDate = new Date(currentYear, currentMonth - 1, 1);
    const prevMonth = prevMonthDate.getMonth();
    const prevMonthYear = prevMonthDate.getFullYear();

    const currentSales = calculateMonthlyTotal(data, currentMonth, currentYear);
    const previousSales = calculateMonthlyTotal(data, prevMonth, prevMonthYear);
    const growth = calculateGrowthRate(previousSales, currentSales);

    setCurrentMonthSales(currentSales);
    setPreviousMonthSales(previousSales);
    setSalesGrowth(growth);
  };

  const calculateMonthlyTotal = (data, month, year) => {
    return data.reduce((total, item) => {
      const itemDate = new Date(item.invoicedate);
      if (itemDate.getMonth() === month && itemDate.getFullYear() === year) {
        const orderTotal = item.items.reduce((sum, i) => {
          const itemPrice = Number(i.price);
          const itemTax = i.tax || 0;
          return sum + itemPrice + itemPrice * (itemTax / 100);
        }, 0);
        
        const discountPercentage = item.discount || 0;
        const discountAmount = (orderTotal * discountPercentage) / 100;
        return total + (orderTotal - discountAmount);
      }
      return total;
    }, 0);
  };

  const calculateGrowthRate = (previousValue, currentValue) => {
    if (previousValue === 0) return currentValue === 0 ? 0 : 100;
    return ((currentValue - previousValue) / previousValue) * 100;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'Lkr',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${Math.round(value)}%`;
  };

  const getGrowthColor = (value) => {
    return value >= 0 ? '#38A169' : '#E53E3E';
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 300, mx: 'auto', py: isSmallScreen ? 0.1 : 1 }}>
    {loading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: isSmallScreen ? 1 : 3, height: isSmallScreen ? '80px' : 'auto' }}>
        <l-trio size={isSmallScreen ? "25" : "35"} speed="1.3" color="#4FD1C5"></l-trio>
      </Box>
    ) : (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <Box sx={{ backgroundColor: 'white', borderRadius: '6px', p: isSmallScreen ? 0 : 1.5, display: 'flex', flexDirection: 'column', gap: isSmallScreen ? 0.1 : 1 }}>
          <Typography variant={isSmallScreen ? "subtitle2" : "subtitle1"} sx={{ fontWeight: 600, color: '#2D3748', fontSize: isSmallScreen ? '0.55rem' : '0.95rem' }}>
            Sales Overview
          </Typography>
  
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: isSmallScreen ? 0.2 : 0.8 }}>
            <Box sx={{ minWidth: '100px' }}>
              <Typography variant="caption" sx={{ color: '#718096', fontSize: isSmallScreen ? '0.65rem' : '0.75rem' }}>
                Current Month
              </Typography>
              <Typography variant={isSmallScreen ? "body1" : "h6"} sx={{ fontWeight: 700, fontSize: isSmallScreen ? '0.9rem' : '1.15rem' }}>
                {formatCurrency(currentMonthSales)}
              </Typography>
            </Box>
  
            <Box sx={{ textAlign: 'right', minWidth: '100px' }}>
              <Typography variant="caption" sx={{ color: '#718096', fontSize: isSmallScreen ? '0.65rem' : '0.75rem' }}>
                Last Month
              </Typography>
              <Typography variant={isSmallScreen ? "body2" : "body1"} sx={{ fontWeight: 500, fontSize: isSmallScreen ? '0.8rem' : '0.95rem' }}>
                {formatCurrency(previousMonthSales)}
              </Typography>
            </Box>
          </Box>
  
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: isSmallScreen ? 0.1 : 0.8 }}>
            <Typography variant="caption" sx={{ color: '#718096', fontSize: isSmallScreen ? '0.65rem' : '0.75rem' }}>
              Growth
            </Typography>
            <Typography variant={isSmallScreen ? "body2" : "body1"} sx={{ fontWeight: 600, color: getGrowthColor(salesGrowth), display: 'flex', alignItems: 'center', fontSize: isSmallScreen ? '0.8rem' : '0.95rem' }}>
              {formatPercentage(salesGrowth)}
              <Box component="span" sx={{ ml: 0.3, width: isSmallScreen ? 8 : 10, height: isSmallScreen ? 8 : 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                {salesGrowth >= 0 ? '↑' : '↓'}
              </Box>
            </Typography>
          </Box>
  
          <Box sx={{ width: '100%', height: isSmallScreen ? 3 : 5, bgcolor: '#EDF2F7', borderRadius: 2, overflow: 'hidden', mt: isSmallScreen ? 0.1 : 0.8 }}>
            <Box sx={{ width: `${Math.min(Math.abs(salesGrowth), 100)}%`, height: '100%', bgcolor: getGrowthColor(salesGrowth), borderRadius: 2 }} />
          </Box>
        </Box>
      </motion.div>
    )}
  </Box>
    );
};

export default FinancialStatsCards;